/* eslint-disable max-len */
import {React, useCallback, useEffect, useState} from "react";
import ReportTable from "./Components/ReportTable";
import Filter from "./Components/Filter";
import { GetBranchId } from "../../../../../../CommonMethods/GetBranchId";
import { dataService } from "../../../../../../services/data.services";
import Shimmer from '../../../../../../components/Shimmer/itemShimmer'
import SearchEmptyScreen from "../../../../../../components/SearchEmptyScreen";

export default function AcademicReports({tabs, searchParams, setSearchParams, setToastErrorMessage}) {
    const [loading, setLoading] = useState(false)
    const [ayAndClassOptions, setAyAndClassOptions] = useState([])
    const [sectionOptions, setSectionOptions] = useState([])
    const [examOptions, setExamOptions] = useState([])
    const [reportsTotalCount, setReportsTotalCount] = useState(0)
    const [records, setRecords] = useState([])
    const [btnLoading, setBtnLoading] = useState(false)
    const [selectedAY, setSelectedAY] = useState([])
    const [selectedSection, setSelectedSection] = useState([])
    const [selectedExam, setSelectedExam] = useState([])

    /**
     * The function `getDefaultValue` returns a default value based on specified conditions from an array
     * of options.
     * @returns The function `getDefaultValue` returns different values based on the conditions provided:
     */
    const getDefaultValue = (options, firstIndex, paramsBased, useParams) => {
        if (!options || options?.length === 0) {
            return ""; // Return empty string if no objects
        }
        if (useParams) {
            if (paramsBased === 'ayAndClass') {
                const ayId = searchParams.get('ay_id');
                const classId = searchParams.get('class_id');
                if (ayId && classId) {
                    const selectedOption = options.find(option => option?.academic_year_id === ayId && option?.class_id === classId);
                    return selectedOption || ''; // Return matching option or empty string if not found
                }
            } else if (paramsBased === 'section') {
                const secId = searchParams.get('sec_id');
                if (secId) {
                    const selectedOption = options.find(option => option?.section_id === secId);
                    return selectedOption || ''; // Return matching option or empty string if not found
                }
            } else if (paramsBased === 'exam') {
                const examId = searchParams.get('exam_id');
                if (examId) {
                    const selectedOption = options.find(option => option?.exam_id === examId);
                    return selectedOption || ''; // Return matching option or empty string if not found
                }
            }
        }
        if (options?.length === 1) {
            return options?.[0];
        }
        const latestOption = options?.find(option => option?.latest === true);
        if (latestOption) {
            return latestOption; // Return the object with the latest key set to true
        } else if (firstIndex) {
            return options?.[0] || ''; // Return the first object if all latest keys are false
        } else {
            return '';
        }
    };

    /* The `getAyAndClassOptions` function is a `useCallback` function that is responsible for fetching
    academic year and class options based on certain parameters. Here is a breakdown of what the
    function is doing: */
    const getAyAndClassOptions = useCallback(async () => {
        try {
            records?.length === 0 && setLoading(true);
            const branch_id = await GetBranchId();
            let endpoint = `students/list-ay-class?branch_id=${branch_id}&student_id=${searchParams?.get('std_uuid')}`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data;
            const temp2 = response?.data?.data?.total_count;
            setAyAndClassOptions(temp);
            const useParams = searchParams?.get('dropdown') === 'true';
            const selectedAYClass = await getDefaultValue(temp, true, 'ayAndClass', useParams);
            if (temp2 !== 0) {
                if (selectedAYClass === '') {
                    getSectionOptions(encodeURIComponent(searchParams?.get('ay_id')), encodeURIComponent(searchParams?.get('class_id')), false, useParams);
                } else {
                    setSelectedAY(selectedAYClass);
                    getSectionOptions(selectedAYClass.academic_year?.academic_year_id, selectedAYClass.class_id, false, useParams);
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            setAyAndClassOptions([]);
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong");
        }
    }, [searchParams]);    

    /* The `getSectionOptions` function is a `useCallback` function that is responsible for fetching
    section options based on the provided academic year ID and class ID. Here is a breakdown of what the
    function is doing: */
    const getSectionOptions = useCallback(async (ayID, classID, fromDropDown) => {
        try {
            const branch_id = await GetBranchId();
            let endpoint = `students/list-sections?branch_id=${branch_id}&student_id=${searchParams?.get('std_uuid')}&academic_year_id=${ayID}&class_id=${classID}`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data;
            setSectionOptions(temp);
    
            const useParams = searchParams?.get('dropdown') === 'true';
            const selectedSection = await getDefaultValue(temp, true, 'section', useParams);
            const selectedIsLatest = selectedSection?.latest || false;
    
            if (selectedSection) {
                setSelectedSection(selectedSection);
                getExamOptions(ayID, classID, selectedSection?.section_id, selectedIsLatest, fromDropDown);
            } else {
                setLoading(false)
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            setSectionOptions([]);
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong");
        }
    }, [searchParams]);
    
    

    /* The `getExamOptions` function is a `useCallback` function that is responsible for fetching exam
    options based on the provided parameters. Here is a breakdown of what the function is doing: */
    const getExamOptions = useCallback(async (ayID, classID, sectionID, isLatest, fromDropDown) => {
        try {
            const branch_id = await GetBranchId();
            let endpoint = `students/list-exams?branch_id=${branch_id}&student_uuid=${searchParams?.get('std_uuid')}&academic_year_id=${ayID}&class_id=${classID}&section_id=${sectionID}&latest=${isLatest}&filter=true`;
            const response = await dataService.GetAPI(endpoint, true);
            let temp;
    
            if (isLatest) {
                temp = response?.data?.data?.data[0]?.exams;
            } else {
                temp = response?.data?.data?.data;
            }
    
            const useParams = searchParams?.get('dropdown') === 'true';
            const selectedExam = await getDefaultValue(temp, true, 'exam', useParams);
    
            setExamOptions(temp);
    
            if (selectedExam) {
                setSelectedExam(selectedExam);
    
                if (!fromDropDown) {
                    getAcademicReports(ayID, classID, sectionID, selectedExam?.exam_id);
                }
    
                if (fromDropDown) {
                    setLoading(false)
                }
            } else {
                setLoading(false)
            }
        } catch (error) {
            console.log(error);
            setExamOptions([]);
            setLoading(false);
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong");
        }
    }, [searchParams]);    

    /* The `getAcademicReports` function is a `useCallback` function that is defined to fetch academic
    reports data based on the provided parameters. Here is a breakdown of what the function is doing: */
    const getAcademicReports = useCallback(async (ayID, classID, sectionID, examID) => {
        try {   
            const branch_id = await GetBranchId()
            let endpoint = `academic-reports?branch_id=${branch_id}&student_id=${searchParams?.get('std_id')}&academic_year_id=${ayID}&class_id=${classID}&section_id=${sectionID}&exam_id=${examID}&filter=true&role=directory`;
            const response = await dataService.GetAPI(endpoint)
            const temp = response?.data?.data?.academicReports?.results
            const temp2 = response?.data?.data?.total_count || 0
            setRecords(temp)
            setReportsTotalCount(temp2)
        } catch(error) {
            console.log(error)
            setLoading(false)
            setRecords([])
            setBtnLoading(false)
            setReportsTotalCount(0)
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong")
        } finally{
            setLoading(false)
            setBtnLoading(false)
        }
    }, []);

    /* The `useEffect` hook in the provided code snippet is used to perform side effects in function
    components. In this specific case: */
    useEffect(()=>{
        getAyAndClassOptions()
    }, [])

    return (
        <div className="">
            <div className="relative">
                <div
                    class="z-20 bg-white mt-5"
                    id="tabs-viewAcadamic_records"
                    role="tabpanel"
                    aria-labelledby="tabs-viewAcadamic_records-tab"
                >
                    <div className="pb-[32px] w-[850px] 2xl:px-0 2xl:w-[80%] mx-auto">
                        <Filter ayAndClassOptions={ayAndClassOptions} sectionOptions={sectionOptions} examOptions={examOptions} searchParams={searchParams}
                            getDefaultValue={getDefaultValue} getAcademicReports={getAcademicReports} loading={loading} reportsTotalCount={reportsTotalCount}
                            getSectionOptions={getSectionOptions} getExamOptions={getExamOptions} setSearchParams={setSearchParams} setLoading={setLoading}
                            btnLoading={btnLoading} setBtnLoading={setBtnLoading} selectedAY={selectedAY} setSelectedAY={setSelectedAY} selectedSection={selectedSection}
                            setSelectedSection={setSelectedSection} selectedExam={selectedExam} setSelectedExam={setSelectedExam} setToastErrorMessage={setToastErrorMessage}
                        />
                    </div>     
                </div>
                {loading ? 
                    <div className='px-10'><Shimmer/> </div>
                    : <ReportTable data={{records, reportsTotalCount}}/>}    
            </div>
        </div>
    );
}
